import { Compass3D } from 'features/assetViz/objects/decor/compass/Compass3D'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { ConeCourseFeature3D } from 'features/assetViz/objects/features/cone/ConeCourseFeature3D'
import { FeatureDispatch } from 'features/assetViz/objects/features/FeatureDispatch'
import { radialConeGeometry } from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  ComponentSurfaceWrappingMode,
  GeckoAssetModel,
  GeckoComponentRect,
  GeckoConeCircumferentialWeld,
  GeckoConeRadialWeld,
  GeckoConicalPlate,
  GeckoLegacyCone,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { Surface3D, Surface3DGroupProps } from './Surface3D'

/**
 * A cone, plotted on a radial plane, with the center of its base at "position"
 * @param model               The cone component model
 * @param assetModel          The asset model for the asset that the cone is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const LegacyConeSurface3D: React.FC<
  {
    model: GeckoLegacyCone
    assetModel: GeckoAssetModel
    plantNorthCompassOrientation?: number
  } & Surface3DGroupProps
> = ({ model, assetModel, ...groupProps }) => {
  //----------------------------------------------------------------------------
  const { bounds, scale } = React.useMemo(() => {
    // radial plot should always start at radius 0 and x range should always span 0 -> 360

    const bounds = GeckoComponentRect.CreateFromXYs(
      model.componentBounds.componentID,
      0,
      0,
      360,
      model.componentBounds.y.end.num,
      GeckoMeasurementUnit.Degrees,
      model.componentBounds.y.unit
    )

    return {
      bounds,
      scale: extractScale(groupProps.scale).multiply(
        new Vector3(
          model.componentBounds.y.end.num,
          model.componentBounds.y.end.num,
          1.0
        )
      ),
    }
  }, [model, groupProps.scale])

  const { plates, verticalWelds, horizontalWelds, courses } =
    React.useMemo(() => {
      const plates: Array<GeckoConicalPlate> =
        model instanceof GeckoLegacyCone ? model.platesArray : []

      const verticalWelds: Array<GeckoConeRadialWeld> =
        model instanceof GeckoLegacyCone ? model.coneRadialWeldsArray : []
      const horizontalWelds: Array<GeckoConeCircumferentialWeld> =
        model instanceof GeckoLegacyCone
          ? model.coneCircumferentialWeldsArray
          : []

      return {
        plates: plates,
        verticalWelds: verticalWelds,
        horizontalWelds: horizontalWelds,
        courses: model.coursesArray,
      }
    }, [model])

  const getCompassPosition = React.useCallback<(angle: number) => Vec3>(
    (angle) => {
      const radians = (angle * Math.PI) / 180
      return [-1.05 * Math.sin(radians), 1.05 * Math.cos(radians), 0]
    },
    []
  )

  //----------------------------------------------------------------------------

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const radius = 1.05
      const radialXPosition = radius * -Math.sin((coord.num * Math.PI) / 180)
      const radialYPosition = radius * Math.cos((coord.num * Math.PI) / 180)
      return [radialXPosition, radialYPosition, 0]
    },
    []
  )

  //----------------------------------------------------------------------------

  return (
    <Surface3D
      geometry={radialConeGeometry}
      bounds={bounds}
      wrapMode={ComponentSurfaceWrappingMode.ComponentWrapsX}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <Compass3D
        componentModel={model}
        assetModel={assetModel}
        getRotationPosition={getCompassPosition}
      />

      {model instanceof GeckoLegacyCone
        ? courses.map((course) => (
            <ConeCourseFeature3D
              key={course.id}
              component={model}
              model={course}
            />
          ))
        : null}
      <FeatureDispatch features={[plates, verticalWelds, horizontalWelds]} />
      {/* Circumferential (x) ruler ---------------------------------------- */}
      <Ruler3D
        anchor='center'
        start={bounds.start.x}
        end={bounds.end.x}
        includeTicks={true}
        includeStart={true}
        includeEnd={false}
        getTickPosition={positionXTick}
      />
    </Surface3D>
  )
}
